(() => {
  /* Propriété de ligne marquant le cadeau. Le souligné la masque au client. */
  const GIFT_PROPERTY = '_gift';

  /* Le client peut retirer son cadeau. On s'en souvient pour la session, sinon le
     bouton supprimer se déferait sous ses yeux. Le drapeau tombe dès que le panier
     repasse sous le seuil : franchir le seuil à nouveau redonne le cadeau. */
  const DECLINED_KEY = 'skorpios:gift-declined';

  const declined = {
    get() {
      try {
        return window.sessionStorage.getItem(DECLINED_KEY) === '1';
      } catch (error) {
        return false;
      }
    },
    set(value) {
      try {
        if (value) window.sessionStorage.setItem(DECLINED_KEY, '1');
        else window.sessionStorage.removeItem(DECLINED_KEY);
      } catch (error) {
        /* navigation privée : on se passe de la mémoire */
      }
    }
  };

  class CartGiftThreshold extends HTMLElement {
    connectedCallback() {
      this.threshold = parseInt(this.getAttribute('threshold'), 10);
      this.variantId = this.getAttribute('variant-id');
      /* Hors période, l'élément ne fait plus que retirer les cadeaux déjà en panier. */
      this.active = this.getAttribute('active') !== 'false';
      this.busy = false;

      if (this.variantId === null || !(this.threshold > 0)) return;

      document.addEventListener('cart:change', (event) => {
        const cart = event.detail && event.detail.cart;
        if (cart == null) return;

        const lines = cart.items || [];
        const gift = lines.find((line) => line.properties != null && line.properties[GIFT_PROPERTY] != null);
        const total = lines.reduce((sum, line) => (line === gift ? sum : sum + line.final_line_price), 0);

        this.apply(
          total,
          gift == null ? null : { key: gift.key, quantity: gift.quantity, variantId: String(gift.variant_id) }
        );
      });

      /* État initial rendu par Liquid : pas de requête au chargement. */
      const key = this.getAttribute('gift-key');

      this.hadGift = Boolean(key);

      this.apply(
        parseInt(this.getAttribute('cart-total'), 10) || 0,
        key
          ? {
              key: key,
              quantity: parseInt(this.getAttribute('gift-quantity'), 10) || 0,
              variantId: this.getAttribute('gift-line-variant') || ''
            }
          : null
      );
    }

    apply(eligibleTotal, gift) {
      if (this.busy) return;

      const qualifies = this.active && eligibleTotal >= this.threshold;

      /* Cadeau présent puis absent alors que le seuil tient toujours : c'est le client
         qui l'a retiré, personne d'autre ne touche à cette ligne. */
      if (qualifies && gift === null && this.hadGift) declined.set(true);
      if (!qualifies) declined.set(false);

      this.hadGift = gift !== null;

      if (qualifies && gift === null && declined.get()) return;

      /* Le produit cadeau a changé dans les réglages : on retire l'ancien. Le passage
         suivant, panier sans cadeau, ajoutera le nouveau. */
      if (gift !== null && gift.variantId && gift.variantId !== String(this.variantId)) {
        this.mutate('change', { id: gift.key, quantity: 0 });
        return;
      }

      if (qualifies && gift === null) {
        const properties = { [GIFT_PROPERTY]: 'true' };

        /* Propriété sans souligné : Shopify l'affiche au checkout, que le thème ne rend pas. */
        const labelKey = this.getAttribute('label-key');

        if (labelKey) properties[labelKey] = this.getAttribute('label-value') || '';

        this.mutate('add', { items: [{ id: this.variantId, quantity: 1, properties }] });
      } else if (!qualifies && gift !== null) {
        this.mutate('change', { id: gift.key, quantity: 0 });
      } else if (qualifies && gift !== null && gift.quantity !== 1) {
        /* Le cadeau est offert à l'unité : on ramène toute quantité ajoutée à la main. */
        this.mutate('change', { id: gift.key, quantity: 1 });
      }
    }

    async mutate(endpoint, body) {
      this.busy = true;

      try {
        /* Le thème collecte ici les sections à re-rendre (tiroir panier, compteur...). */
        const sections = new Set();

        document.documentElement.dispatchEvent(
          new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } })
        );

        const response = await fetch(`${Shopify.routes.root}cart/${endpoint}.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ ...body, sections: [...sections].join(',') })
        });

        if (!response.ok) return;

        const payload = await response.json();

        /* Sur /cart, le thème recharge la page à chaque changement : on fait pareil,
           sinon les totaux affichés ne suivraient pas. */
        if (window.themeVariables?.settings?.pageType === 'cart') {
          window.location.reload();
          return;
        }

        const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
        cart.sections = payload.sections;

        /* Diffusé pendant que busy vaut encore true : notre propre écouteur ressort
           aussitôt, ce qui coupe la boucle. Les autres (tiroir, compteur) travaillent. */
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'gift:update', cart } })
        );
      } finally {
        this.busy = false;
      }
    }
  }

  if (window.customElements.get('cart-gift-threshold') == null) {
    window.customElements.define('cart-gift-threshold', CartGiftThreshold);
  }
})();
